// ========================================
// 共通型定義・定数
// ========================================

// サービス種別（API v1対応）
export type ServiceType = "default" | "custom" | "user_owned";

// ユーザーステータス
export type UserStatus = "active" | "suspended";
export const USER_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended",
} as const;

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: "アクティブ",
  suspended: "停止中",
};

// ユーザーロール
export type UserRole = "user" | "admin";
export const USER_ROLE = {
  USER: "user",
  ADMIN: "admin",
} as const;

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  user: "ユーザー",
  admin: "管理者",
};

// ユーザープラン（バックエンドの PLAN_TYPES と同期）
export const PLAN_TYPES = ["free", "pro", "campaign_pro"] as const;
export type PlanType = (typeof PLAN_TYPES)[number];

// ソートキー
export type UserSortBy =
  | "email"
  | "name"
  | "createdAt"
  | "lastLoginAt"
  | "lastClickAt"
  | "totalClicks";

// ========================================
// ユーザー関連型定義
// ========================================

export interface User {
  id: string;
  email: string;
  role?: UserRole;
}

export interface UserSettings {
  id: string;
  email: string;
  role: UserRole;
}

// 設定ページ用ユーザープロフィール型定義
export interface UserProfile {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string | null;
  image: string | null;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  totalClicks: number;
  lastClickAt: string | null;
}

// 管理者用ユーザー型定義
export interface AdminUser {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string | null;
  image: string | null;
  role: UserRole;
  status: UserStatus;
  plan: PlanType;
  campaignProGrantedAt: string | null;
  lastLoginAt: string | null;
  totalClicks: number;
  lastClickAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  adminUsers: number;
  regularUsers: number;
  usersWithLogin: number;
  usersWithClicks: number;
  averageClicks: number;
}

export interface UsersPaginatedResponse {
  data: AdminUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// タグのファセット分類（バックエンド schema.ts の TAG_CATEGORIES と対応）
// フィルタ UI のグループ表示順もこの並びに従う
export const TAG_CATEGORIES = ["ecosystem", "function", "attribute"] as const;
export type TagCategory = (typeof TAG_CATEGORIES)[number];

export interface Tag {
  id: string;
  name: string;
  nameEn?: string | null; // 英語名（i18n対応）
  description?: string | null;
  color?: string | null;
  sortOrder: number;
  category?: TagCategory | null; // ファセット分類（サービス側 payload には含まれないため optional。判定は getTagCategory を使う）
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customizationUserCount?: number; // カスタマイズしているユーザー数（管理者用）
}

// Service links type
// 並び順: 公式サイト → 情報系 → 開発系 → SNS → コミュニティ → レビュー
export interface ServiceLinks {
  // 公式サイト
  home?: string;
  homeEn?: string;
  // 情報系（技術系 → 最新情報 → 一般向け）
  document?: string;
  api?: string;
  changelog?: string;
  blog?: string;
  price?: string;
  learn?: string;
  faq?: string;
  // 開発系
  github?: string;
  // SNS
  x?: string;
  linkedin?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  // コミュニティ
  discord?: string;
  youtube?: string;
  // レビュー
  productHunt?: string;
}

export interface APIResponse<T = unknown> {
  data?: T;
  message?: string;
  error?: string;
}

// 基本情報自動入力APIのレスポンス型
export interface AutoFillBasicInfoResponse {
  provider: string;
  description: string;
  releaseDate: string;
  devCountry: string;
  suggestedTagIds: string[];
}

export interface ServiceFormData {
  name: string;
  provider: string;
  url: string;
  logoUrl?: string;
  description: string;
  releaseDate: string;
  devCountry?: string;
  personalNotes?: string;
}

export interface LoginFormData {
  email: string;
  password: string;
}

export interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

export type SortBy = "name" | "created" | "favorite";
export type SortOrder = "asc" | "desc";

/**
 * 検索対象の範囲。
 * - `all`: 名前・提供元・タグ・説明文（既定）
 * - `name`: 名前・提供元のみ
 *
 * Fuse.js の weight は並び順にしか効かず、ヒット判定は全キー同列。そのため「Codex」のように
 * 多くのサービスの説明文へ現れる語は、既定スコープだと名前一致がノイズに埋もれる。
 * スコープを切り替えて説明文・タグをヒット判定から外せるようにする
 */
export type SearchScope = "all" | "name";

export interface FilterState {
  searchQuery: string;
  searchScope: SearchScope;
  selectedTags: string[];
  sortBy: SortBy;
  sortOrder: SortOrder;
}

// ========================================
// 管理サービス共通ベース型（default + custom）
// ========================================
interface ManagedServiceBase {
  id: string;
  userId?: string;
  name: string;
  provider: string;
  url: string;
  affiliateUrl: string | null;
  logoUrl: string | null;
  description: string;
  releaseDate: string;
  devCountry: string | null;
  mcpDocsUrl: string | null;
  /**
   * サービス終了日（ISO 日付）。終了済みのサービスはお気に入りに入れている場合だけ
   * デッキに残り、カードに「終了」バッジが付く。省略可: 旧レスポンス・テスト用フィクスチャ互換
   */
  discontinuedAt?: string | null;
  discontinuedSourceUrl?: string | null;
  isFavorite: boolean;
  isHidden: boolean;
  isEditorPick: boolean;
  isTrending: boolean;
  isPopular: boolean;
  isCustomized: boolean;
  basePersonalNotes: string | null; // Layer 2: 組織メモ
  userPersonalNotes: string | null; // Layer 3: ユーザーメモ
  links: ServiceLinks | null;
  dragBarColor: string | null;
  sortOrder: number;
  favoriteOrder?: number | null;
  favoriteArea?: number | null;
  favoriteCount?: number;
  createdAt: string;
  updatedAt: string;
  defaultUpdatedAt: string;
  customUpdatedAt: string | null;
  tags: Tag[];
}

/** デフォルトサービス（Layer 1 + Layer 2a + Layer 3a） */
export interface DefaultService extends ManagedServiceBase {
  serviceType: "default";
}

/** カスタムサービス（Layer 2b + Layer 3b） */
export interface CustomService extends ManagedServiceBase {
  serviceType: "custom";
}

/** デフォルト + カスタムサービスの Union 型 */
export type ManagedService = DefaultService | CustomService;

export interface ServiceRequest {
  id: string;
  userId: string;
  serviceName: string;
  serviceUrl: string;
  description: string;
  reasoning: string;
  categorySuggestion?: string | null;
  status: string;
  adminNotes?: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceRequestFormData {
  serviceName: string;
  serviceUrl: string;
  description?: string;
  reasoning?: string;
  categorySuggestion?: string;
}

export interface ServiceOverrideFormData {
  customName?: string;
  customDescription?: string;
  personalNotes?: string;
}

// 管理画面専用のサービス型
export interface AdminService {
  id: string;
  name: string;
  provider: string;
  url: string;
  affiliateUrl?: string | null;
  logoUrl: string | null;
  description: string | null;
  descriptionEn: string | null;
  releaseDate: string | null;
  devCountry: string | null;
  mcpDocsUrl: string | null;
  personalNotes: string | null;
  personalNotesEn: string | null;
  links: ServiceLinks | null;
  sortOrder: number;
  isActive: boolean;
  isEditorPick: boolean;
  isTrending: boolean;
  isPopular: boolean;
  hasOverride?: boolean;
  favoriteCount?: number; // いいね数（集計キャッシュから取得）
  clickCount?: number; // クリック数（service_click_counts から取得）
  createdAt: string;
  updatedAt: string;
  tags: Tag[];
}

// サービス作成用の型（自動生成フィールドを除外）
export type CreateServiceData = Omit<
  AdminService,
  "createdAt" | "updatedAt" | "hasOverride" | "favoriteCount" | "clickCount" | "tags"
> & {
  tagIds: string[]; // タグIDの配列
};

// サービス更新用の型（自動生成フィールドを除外し、全フィールドをオプショナルに）
export type UpdateServiceData = Partial<
  Omit<AdminService, "id" | "createdAt" | "updatedAt" | "tags">
> & {
  tagIds?: string[]; // タグIDの配列（オプショナル）
};

// ユーザー更新用の型（管理者用）
export type UpdateAdminUserData = Partial<
  Omit<
    AdminUser,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "emailVerified"
    | "totalClicks"
    | "lastClickAt"
    | "lastLoginAt"
    | "campaignProGrantedAt"
  >
>;

// サービスオーバーライド更新用の型（一般ユーザー向け継承システム）
export interface UpdateServiceOverrideData {
  customName?: string | null;
  customProvider?: string | null;
  customUrl?: string | null;
  customLogoUrl?: string | null;
  customDescription?: string | null;
  customReleaseDate?: string | null;
  customDevCountry?: string | null;
  personalNotes?: string | null;
  personalNotesEn?: string | null;
}

// ========================================
// ユーザー独自サービス関連型定義
// ========================================

/**
 * ユーザー独自サービス情報
 * - ユーザーが自分で追加したプライベートなサービス
 * - 他のユーザーには表示されない
 * - 1ユーザー最大100件まで
 */
export interface UserOwnedService {
  id: string;
  userId: string;
  name: string;
  provider: string | null;
  url: string;
  logoUrl: string | null;
  description: string | null;
  releaseDate: string | null;
  devCountry: string | null;
  mcpDocsUrl?: null; // Layer 4 はカタログ属性を持たない（常に null）
  personalNotes: string | null;
  sortOrder: number;
  isActive: boolean;
  isFavorite: boolean;
  favoriteOrder: number | null;
  favoriteArea: number | null;
  dragBarColor: string | null;
  createdAt: string;
  updatedAt: string;
  favoriteCount: number;
  tags: Tag[];
  serviceType: "user_owned"; // API v1対応: サービス種別
  isUserOwned: true; // 識別用フラグ
}

// ユーザーサービス作成用の型
export interface CreateUserServiceData {
  name: string;
  url: string;
  provider?: string | null;
  logoUrl?: string | null;
  description?: string | null;
  releaseDate?: string | null;
  devCountry?: string | null;
  personalNotes?: string | null;
  tagIds?: string[];
}

// ユーザーサービス更新用の型
export interface UpdateUserServiceData {
  name?: string;
  url?: string;
  provider?: string | null;
  logoUrl?: string | null;
  description?: string | null;
  releaseDate?: string | null;
  devCountry?: string | null;
  personalNotes?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  dragBarColor?: string | null;
  tagIds?: string[];
}

// ユーザーサービス数のレスポンス
export interface UserServiceCountResponse {
  count: number;
  max: number;
  remaining: number;
}

// ========================================
// API v1: 統一サービス状態管理型定義
// ========================================
// ServiceType は共通型定義セクション（ファイル先頭）で定義済み

/**
 * サービス状態（user_service_states テーブル）
 */
export interface ServiceState {
  serviceId: string;
  serviceType: ServiceType;
  isFavorite: boolean;
  isHidden: boolean;
  favoriteOrder: number | null;
  favoriteArea: number;
  dragBarColor: string | null;
}

/**
 * サービス状態更新データ
 */
export interface ServiceStateUpdate {
  isFavorite?: boolean;
  isHidden?: boolean;
  favoriteOrder?: number | null;
  favoriteArea?: number;
  dragBarColor?: string | null;
}

/**
 * サービス状態 + 基本情報（favorites/hidden 一覧用）
 */
export interface ServiceStateWithInfo extends ServiceState {
  name: string;
  provider: string;
  url: string;
  logoUrl: string | null;
  description: string;
}

// サービス一覧のユニオン型（デフォルト/カスタム + ユーザー独自）
export type AnyService = DefaultService | CustomService | UserOwnedService;

// ========================================
// サービス追加リクエスト関連
// ========================================

// 重複チェックレスポンス
export interface DuplicateCheckResponse {
  isDuplicate: boolean;
  existingService?: {
    id: string;
    name: string;
    type: "default" | "custom";
  };
  existingRequest?: {
    id: string;
    name: string;
  };
  message?: string;
}

// 管理者用申請データ
export interface AdminServiceRequest {
  id: string;
  userId: string | null;
  serviceName: string;
  serviceUrl: string;
  description: string;
  reasoning: string | null;
  categorySuggestion: string | null;
  status: string;
  adminNotes: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  createdAt: string;
  updatedAt: string;
  userEmail: string | null;
  userName: string | null;
}

// 管理者用申請一覧レスポンス
export interface AdminServiceRequestsResponse {
  requests: AdminServiceRequest[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ========================================
// アクティビティログ関連型定義
// ========================================

/**
 * アクティビティログのアクションタイプ
 */
export type ActivityLogActionType =
  | "STATUS_CHANGE"
  | "BULK_STATUS_CHANGE"
  | "BULK_DELETE"
  | "PERMANENT_DELETE"
  | "PLAN_CHANGE"
  | "LOGIN_SUCCESS"
  | "LOGIN_FAILED"
  | "DETAILS_EDIT"
  | "EXPORT"
  | "VIEW";

/**
 * アクティビティログのメタデータ
 */
export interface ActivityLogMetadata {
  targetUserEmail?: string;
  [key: string]: unknown;
}

/**
 * アクティビティログ
 */
export interface ActivityLog {
  id: number;
  adminUserId: string;
  targetUserId: string;
  actionType: ActivityLogActionType;
  oldValue: string | null;
  newValue: string | null;
  description: string | null;
  metadata: ActivityLogMetadata | null;
  ipAddress: string | null;
  userAgent: string | null;
  timestamp: number;
  requestId: string | null;
}

/**
 * アクティビティログ一覧レスポンス
 */
export interface ActivityLogResponse {
  data: ActivityLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ========================================
// バナー（サービス一覧バナー枠）
// ========================================

/**
 * 公開バナー（GET /public/banners のレスポンス要素）
 * startsAt / endsAt は epoch ms（TZ 非依存）。並び順は配列位置で保証されるため sortOrder は持たない。
 */
export interface PublicBanner {
  id: string;
  imageUrl: string;
  alt: string;
  altEn: string | null;
  linkUrl: string;
  isAd: boolean;
  startsAt: number | null;
  endsAt: number | null;
}

/**
 * 管理バナー（GET /api/admin/banners のレスポンス要素）
 * 公開バナー + is_active。タイムスタンプは epoch ms に正規化して返す。
 */
export interface Banner extends PublicBanner {
  isActive: boolean;
}

/** バナー作成 / 更新のペイロード（sortOrder は API 側で採番） */
export interface BannerFormPayload {
  imageUrl: string;
  alt: string;
  altEn: string | null;
  linkUrl: string;
  isAd: boolean;
  isActive: boolean;
  startsAt: number | null;
  endsAt: number | null;
}
